import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, User, Tag, Facebook, Instagram, Share2, MapPin, Star, Heart } from 'lucide-react';
import { motion } from 'framer-motion';
import { FacebookShareButton, TwitterShareButton, WhatsappShareButton } from 'react-share';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';

const UgandaTribesExperiences = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Cultural Experiences: Meeting Uganda\'s Tribes | TravelCord Blog';
  }, []);

  const handleShare = async (platform: string) => {
    const url = window.location.href;
    const title = "Cultural Experiences: Meeting Uganda's Tribes";

    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'instagram':
        alert('Follow us on Instagram @travelcord.guide');
        break;
      default:
        if (navigator.share) {
          try {
            await navigator.share({ title, url });
          } catch (error) {
            console.error('Error sharing:', error);
          }
        }
    }
  };

  return (
    <>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white">
        <div className="absolute inset-0">
          <img 
            src="https://imgur.com/5v6lVl8.jpg"
            alt="Uganda Tribes"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/80"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Link to="/blog" className="inline-flex items-center text-white/90 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Blog
            </Link>
          </div>
          
          <div className="max-w-3xl">
            <div className="flex items-center mb-4 space-x-4">
              <img 
                src="https://imgur.com/9i40c9u.jpg"
                alt="John Mukasa"
                className="h-12 w-12 rounded-full border-2 border-white"
              />
              <div>
                <h3 className="text-lg font-medium">John Mukasa</h3>
                <p className="text-white/80 text-sm">Cultural Guide</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="inline-flex items-center bg-ta-green/20 rounded-full px-2.5 py-0.5 text-xs text-ta-green mb-4 backdrop-blur-sm">
                Culture
              </div>
              
              <h1 className="text-4xl font-bold">Cultural Experiences: Meeting Uganda's Tribes</h1>
              
              <div className="flex flex-wrap gap-4 text-sm text-white/80">
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  March 5, 2025
                </div>
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  9 min read
                </div>
                <div className="flex items-center">
                  <MapPin className="h-4 w-4 mr-1" />
                  Multiple Locations
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="prose max-w-none">
                <p className="lead text-lg text-gray-600">
                  Uganda is home to over 50 distinct ethnic groups, each with its own rich traditions, 
                  customs, and way of life. This guide explores some of the most fascinating tribal 
                  cultures you can experience during your visit.
                </p>

                <h2>The Batwa People</h2>
                <p>
                  Known as the "Keepers of the Forest," the Batwa are among Uganda's oldest indigenous 
                  groups. Their traditional lifestyle was deeply connected to the Bwindi Impenetrable 
                  Forest until conservation efforts led to changes in their way of life.
                </p>

                <div className="my-8">
                  <img 
                    src="https://imgur.com/eBF3SoG.jpg" 
                    alt="Batwa Cultural Experience"
                    className="rounded-lg w-full"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    A Batwa elder sharing traditional knowledge with visitors
                  </p>
                </div>

                <h2>The Baganda Kingdom</h2>
                <p>
                  Uganda's largest ethnic group, the Baganda, maintain a rich cultural heritage centered 
                  around their kingdom. Visitors can explore the Kasubi Tombs, a UNESCO World Heritage site, 
                  and experience traditional ceremonies.
                </p>

                <h2>The Karamojong</h2>
                <p>
                  The semi-nomadic Karamojong people of northeastern Uganda maintain many of their traditional 
                  practices. Their unique culture includes distinctive dress, architecture, and pastoral lifestyle.
                </p>

                <div className="bg-gray-50 p-6 rounded-lg my-8">
                  <h3 className="text-lg font-semibold mb-4">Cultural Etiquette Tips</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <Star className="h-5 w-5 text-ta-green mr-2 flex-shrink-0" />
                      <span>Always ask permission before taking photographs</span>
                    </li>
                    <li className="flex items-start">
                      <Star className="h-5  w-5 text-ta-green mr-2 flex-shrink-0" />
                      <span>Dress modestly when visiting communities</span>
                    </li>
                    <li className="flex items-start">
                      <Star className="h-5 w-5 text-ta-green mr-2 flex-shrink-0" />
                      <span>Learn basic greetings in local languages</span>
                    </li>
                    <li className="flex items-start">
                      <Star className="h-5 w-5 text-ta-green mr-2 flex-shrink-0" />
                      <span>Respect local customs and traditions</span>
                    </li>
                  </ul>
                </div>

                <h2>Cultural Experiences</h2>
                <p>
                  Many communities offer immersive cultural experiences that allow visitors to:
                </p>
                <ul>
                  <li>Participate in traditional dances</li>
                  <li>Learn about local crafts and artistry</li>
                  <li>Experience traditional cooking methods</li>
                  <li>Visit local markets and community projects</li>
                  <li>Learn about traditional medicine and healing</li>
                </ul>

                <h2>Supporting Local Communities</h2>
                <p>
                  When visiting tribal communities, it's important to ensure your tourism supports local 
                  development. Look for community-based tourism initiatives and fair-trade craft shops.
                </p>
              </div>

              {/* Tags */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <div className="flex flex-wrap gap-2">
                  <span className="text-sm text-gray-600 mr-2">Tags:</span>
                  {["Culture", "Tribes", "Local Life", "Traditions", "Community Tourism"].map((tag, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-ta-green hover:text-white transition-colors cursor-pointer"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Share Buttons */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h3 className="text-lg font-semibold mb-4">Share this article</h3>
                <div className="flex space-x-4">
                  <FacebookShareButton
                    url={window.location.href}
                    quote="Cultural Experiences: Meeting Uganda's Tribes"
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Facebook className="h-5 w-5 mr-2" />
                    Facebook
                  </FacebookShareButton>
                  <TwitterShareButton
                    url={window.location.href}
                    title="Cultural Experiences: Meeting Uganda's Tribes"
                    className="flex items-center px-4 py-2 bg-blue-400 text-white rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    Twitter
                  </TwitterShareButton>
                  <WhatsappShareButton
                    url={window.location.href}
                    title="Cultural Experiences: Meeting Uganda's Tribes"
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Share2 className="h-5 w-5 mr-2" />
                    Share
                  </WhatsappShareButton>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Author Card */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center mb-4">
                    <img 
                      src="https://imgur.com/9i40c9u.jpg"
                      alt="John Mukasa"
                      className="h-16 w-16 rounded-full"
                    />
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold">John Mukasa</h3>
                      <p className="text-gray-600">Cultural Guide</p>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">
                    John specializes in cultural tourism and has extensive experience working with 
                    various tribal communities across Uganda.
                  </p>
                  <button className="w-full py-2 bg-ta-green text-white rounded-lg hover:bg-ta-green-dark transition-colors">
                    Follow
                  </button>
                </div>

                {/* Table of Contents */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold mb-4">Table of Contents</h3>
                  <nav className="space-y-2">
                    <a href="#batwa" className="block text-gray-600 hover:text-ta-green">The Batwa People</a>
                    <a href="#baganda" className="block text-gray-600 hover:text-ta-green">The Baganda Kingdom</a>
                    <a href="#karamojong" className="block text-gray-600 hover:text-ta-green">The Karamojong</a>
                    <a href="#experiences" className="block text-gray-600 hover:text-ta-green">Cultural Experiences</a>
                    <a href="#support" className="block text-gray-600 hover:text-ta-green">Supporting Communities</a>
                  </nav>
                </div>

                {/* Related Articles */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold mb-4">Related Articles</h3>
                  <div className="space-y-4">
                    <Link to="/blog/hidden-gems-kampala" className="block group">
                      <div className="flex items-start">
                        <img 
                          src="https://imgur.com/EzHnSsj.jpg"
                          alt="Hidden Gems in Kampala"
                          className="w-20 h-20 rounded-lg object-cover"
                        />
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-gray-900 group-hover:text-ta-green">
                            10 Hidden Gems in Kampala You Can't Miss
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">6 min read</p>
                        </div>
                      </div>
                    </Link>
                    <Link to="/blog/best-time-visit-uganda" className="block group">
                      <div className="flex items-start">
                        <img 
                          src="https://imgur.com/8u8WjGG.jpg"
                          alt="Best Time to Visit Uganda"
                          className="w-20 h-20 rounded-lg object-cover"
                        />
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-gray-900 group-hover:text-ta-green">
                            Best Time to Visit Uganda
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">12 min read</p>
                        </div>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default UgandaTribesExperiences;